import { get } from 'lodash'
import { Box, Divider, Grid, Typography } from '@material-ui/core'
import React, { useEffect, useRef } from 'react'
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  EnergySystemType,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
} from '../../../../generated/graphql'
import {
  InputTabFormValues,
  saveInputData,
  updateModel,
  useInputTabStyles,
} from './input-util'
import { theme } from '../../../../theme'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { validators } from '../../../../validation/validators'
import {
  areaUnit,
  temperatureUnit,
  uValueUnit,
} from '../../../../util/formatters'
import { i18n } from '../../../../i18n'

type InputFieldProps = {
  control: Control<InputTabFormValues>
  errors: DeepMap<InputTabFormValues, FieldError>
  label: string
  fieldName: string
  fieldUnit: string
  valueUpdateMethod: (values: InputTabFormValues) => void
}

function InputField({
  control,
  errors,
  label,
  fieldName,
  fieldUnit,
  valueUpdateMethod,
}: InputFieldProps): React.ReactElement {
  return (
    <ControlledTextField
      control={control}
      name={fieldName}
      error={get(errors, fieldName)} // eslint-disable-line
      label={i18n.t(label)}
      rules={{
        ...validators.required,
        ...validators.decimal,
      }}
      size="small"
      InputProps={{
        endAdornment: <Typography>{fieldUnit}</Typography>,
      }}
      onBlur={() => valueUpdateMethod(control.getValues())}
    />
  )
}

type Props = {
  id: string
  defaultValues: InputTabFormValues | undefined
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export function CalibrationInputSpaceHeatingTab({
  id,
  defaultValues,
  onUpdate,
  onDbUpdate,
}: Props): React.ReactElement {
  const classes = useInputTabStyles()
  const { t } = useTranslation()

  const { control, errors, reset, formState } = useForm<InputTabFormValues>({
    defaultValues,
    mode: 'onBlur',
  })

  const dbUpdateNeededRef = useRef(false)

  const updateModelData = async (values: InputTabFormValues) => {
    await updateModel({
      values,
      defaultValues: defaultValues ?? ({} as InputTabFormValues),
      esType: EnergySystemType.SpaceHeating,
      control,
      formState,
      updateStateRef: dbUpdateNeededRef,
      onReset: reset,
      onUpdate,
    })
  }

  const saveChanges = async () => {
    await saveInputData({ energySystemId: id, control, onDbUpdate })
  }

  useEffect(() => {
    void updateModelData(defaultValues ?? ({} as InputTabFormValues))

    return () => {
      if (dbUpdateNeededRef.current && defaultValues) {
        void saveChanges()
        dbUpdateNeededRef.current = false
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form style={{ marginTop: theme.spacing(2) }}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Box className={classes.inputTab}>
            <Typography>
              {t('energySystem.parameters.spaceHeating.tabs.heatLosses')}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs>
                <Typography variant="body2">
                  {t('energySystem.parameters.spaceHeating.exteriorWallLabel')}
                </Typography>
              </Grid>
              <Grid item xs>
                <Typography variant="body2">
                  {t('energySystem.parameters.spaceHeating.roofLabel')}
                </Typography>
              </Grid>
              <Grid item xs>
                <Typography variant="body2">
                  {t('energySystem.parameters.spaceHeating.baseFloorLabel')}
                </Typography>
              </Grid>
              <Grid item xs>
                <Typography variant="body2">
                  {t('energySystem.parameters.spaceHeating.windowsLabel')}
                </Typography>
              </Grid>
              <Grid item xs>
                <Typography variant="body2">
                  {t('energySystem.parameters.spaceHeating.doorsLabel')}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="unitName.area"
                  fieldName="values.parameters.spaceHeating.exteriorWallArea"
                  fieldUnit={areaUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="unitName.area"
                  fieldName="values.parameters.spaceHeating.roofArea"
                  fieldUnit={areaUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="unitName.area"
                  fieldName="values.parameters.spaceHeating.baseFloorArea"
                  fieldUnit={areaUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="unitName.area"
                  fieldName="values.parameters.spaceHeating.windowsArea"
                  fieldUnit={areaUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="unitName.area"
                  fieldName="values.parameters.spaceHeating.doorsArea"
                  fieldUnit={areaUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.UvalueLabel"
                  fieldName="values.parameters.spaceHeating.exteriorWallUvalue"
                  fieldUnit={uValueUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.UvalueLabel"
                  fieldName="values.parameters.spaceHeating.roofUvalue"
                  fieldUnit={uValueUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.UvalueLabel"
                  fieldName="values.parameters.spaceHeating.baseFloorUvalue"
                  fieldUnit={uValueUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.UvalueLabel"
                  fieldName="values.parameters.spaceHeating.windowsUvalue"
                  fieldUnit={uValueUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.UvalueLabel"
                  fieldName="values.parameters.spaceHeating.doorsUvalue"
                  fieldUnit={uValueUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
            </Grid>
            <Divider
              style={{
                marginTop: theme.spacing(3),
                marginBottom: theme.spacing(2),
              }}
            />
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.heatBridgeRatio"
                  fieldName="values.parameters.spaceHeating.heatBridgeRatio"
                  fieldUnit="%"
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  control={control}
                  name="values.parameters.spaceHeating.q50"
                  error={errors.values?.parameters?.spaceHeating?.q50}
                  label={`${t(
                    'energySystem.parameters.spaceHeating.airLeakage',
                  )} - ${t(
                    'energySystem.parameters.spaceHeating.airLeakageq50',
                  )}`}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  control={control}
                  name="values.parameters.spaceHeating.airLeakageXfactor"
                  error={
                    errors.values?.parameters?.spaceHeating?.airLeakageXfactor
                  }
                  label={`${t(
                    'energySystem.parameters.spaceHeating.airLeakage',
                  )} - ${t(
                    'energySystem.parameters.spaceHeating.airLeakageXfactor',
                  )}`}
                  rules={{
                    ...validators.required,
                    ...validators.positiveDecimal,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Box
            className={classes.inputTab}
            style={{ marginTop: theme.spacing(2) }}
          >
            <Typography>
              {t(
                'energySystem.parameters.spaceHeating.tabs.calculationParameters',
              )}
            </Typography>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={4}>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.affectingHeatLossStartTemp"
                  fieldName="values.parameters.spaceHeating.affectingHeatLossStartTemp"
                  fieldUnit={temperatureUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs={4}>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.dynamicOutTempWeightFactor"
                  fieldName="values.parameters.spaceHeating.dynamicOutTempWeightFactor"
                  fieldUnit="%"
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs={4}>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.heatLossesCorrectionFactor"
                  fieldName="values.parameters.spaceHeating.heatLossesCorrectionFactor"
                  fieldUnit="%"
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs={4}>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.heatLossesCorrectionFactorStartTemp"
                  fieldName="values.parameters.spaceHeating.heatLossesCorrectionFactorStartTemp"
                  fieldUnit={temperatureUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs={4}>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.supplyAirHeatingStartTemp"
                  fieldName="values.parameters.spaceHeating.supplyAirHeatingStartTemp"
                  fieldUnit={temperatureUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
              <Grid item xs={4}>
                <InputField
                  control={control}
                  errors={errors}
                  label="energySystem.parameters.spaceHeating.supplyAirFullHeatingStartTemp"
                  fieldName="values.parameters.spaceHeating.supplyAirFullHeatingStartTemp"
                  fieldUnit={temperatureUnit}
                  valueUpdateMethod={updateModelData}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </form>
  )
}
